import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';

import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import { opcoesDasPaginas } from './api/revisao';

/** Mesma normalização de espaço/quebra de linha que a API faz na conferência do trecho (D4, `DI16` do TP-050). */
function normalizar(texto: string): string {
  return texto.replace(/\s+/gu, ' ').trim();
}

/**
 * Acha o TRECHO LITERAL dentro do texto da página, tolerando só a mesma
 * normalização que a API tolera — nunca mais que isso, para não fingir
 * achado o que a API marcou como não localizado.
 */
function localizarTrecho(textoDaPagina: string, trecho: string): { antes: string; achado: string; depois: string } | null {
  const alvo = normalizar(trecho);
  if (alvo === '') return null;

  // Busca sobre o texto normalizado, depois projeta de volta nos índices do texto ORIGINAL — a
  // página é exibida como veio da API (D5), sem normalizar a visualização inteira.
  const original = textoDaPagina;
  const indice = original.replace(/\s+/gu, ' ').indexOf(alvo);
  if (indice === -1) return null;

  // Para não desenhar o destaque em índice errado (o texto normalizado pode ter comprimento
  // diferente do original por causa de quebras de linha colapsadas), localizamos de novo sobre o
  // texto original com uma expressão regular tolerante a espaço/quebra de linha no lugar de cada
  // espaço do trecho.
  const regexDoTrecho = new RegExp(alvo.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&').replace(/ /gu, '\\s+'), 'u');
  const achado = regexDoTrecho.exec(original);
  if (achado === null) return null;

  return {
    antes: original.slice(0, achado.index),
    achado: achado[0],
    depois: original.slice(achado.index + achado[0].length),
  };
}

/**
 * A página do contrato com o trecho da regra DESTACADO (`TP-050`, T9, `S5`/`F4`).
 *
 * O PDF original não é renderizado aqui — o tech-plan pede o destaque sobre
 * o TEXTO da página (o mesmo que a API guardou e conferiu), não sobre o
 * layout do PDF.
 */
export function PaginaDoContrato({
  fonteId,
  pagina,
  trecho,
  trechoLocalizado,
}: {
  fonteId: string;
  pagina: number;
  trecho: string | null;
  trechoLocalizado: boolean;
}): React.JSX.Element {
  const consulta = useQuery(opcoesDasPaginas(fonteId));

  if (consulta.isPending) return <EstadoDeCarregamento linhas={6} />;

  if (consulta.isError) {
    return (
      <EstadoDeErro
        erro={consulta.error}
        contexto="Não foi possível carregar o texto da página"
        aoReprocessar={() => {
          void consulta.refetch();
        }}
      />
    );
  }

  const paginaEncontrada = consulta.data.find((p) => p.pagina === pagina);

  if (paginaEncontrada === undefined) {
    return (
      <Alert severity="warning" role="alert">
        Página {pagina} não encontrada no texto desta fonte.
      </Alert>
    );
  }

  const destaque = trecho !== null ? localizarTrecho(paginaEncontrada.texto, trecho) : null;

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        Página {pagina}
        {paginaEncontrada.mascarado ? ' · texto mascarado' : ''}
      </Typography>

      {!trechoLocalizado ? (
        <Alert severity="warning" role="alert" sx={{ mt: 1, mb: 1 }}>
          Trecho não localizado nesta página — a confiança caiu para baixa (F4). Confira o texto com
          atenção antes de decidir.
        </Alert>
      ) : null}

      {paginaEncontrada.observacao !== null ? (
        <Alert severity="info" role="status" sx={{ mt: 1, mb: 1 }}>
          Observação da página: {paginaEncontrada.observacao}
        </Alert>
      ) : null}

      <Box
        component="pre"
        aria-label={`Texto da página ${String(pagina)}`}
        sx={(tema) => ({
          whiteSpace: 'pre-wrap',
          fontFamily: tema.typography.fontFamily,
          fontSize: '0.875rem',
          lineHeight: 1.6,
          m: 0,
          p: 1.5,
          maxHeight: 420,
          overflowY: 'auto',
          backgroundColor: tema.palette.background.default,
          border: `1px solid ${tema.palette.divider}`,
          borderRadius: 1,
        })}
      >
        {destaque === null ? (
          paginaEncontrada.texto
        ) : (
          <>
            {destaque.antes}
            <Box
              component="mark"
              aria-label="Trecho literal da regra"
              sx={(tema) => ({
                backgroundColor: tema.palette.sla.atencao.fundo,
                color: tema.palette.sla.atencao.tinta,
                border: `1px solid ${tema.palette.sla.atencao.borda}`,
                borderRadius: 0.5,
                fontWeight: 600,
                px: 0.25,
              })}
            >
              {destaque.achado}
            </Box>
            {destaque.depois}
          </>
        )}
      </Box>
    </Box>
  );
}
